/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React from 'react';
import { Alert, Layout } from 'antd';
import {
  ExclamationCircleOutlined,
  IssuesCloseOutlined
} from '@ant-design/icons';
import {
  IQuestionConfig,
  QuestionAnswerMode,
  RequestQuestionType
} from './interface';
import InlineFacts from '../facts/InlineFacts';
import {
  ReviewConceptQuestion,
  ValueNotKnownQuestion,
  Worker
} from '../../generated/graphql';
import FormattingUtil from '../../utils/FormattingUtil';
import styles from './RequestQuestionTitle.module.less';

interface IRequestQuestionTitleProps {
  question: RequestQuestionType;
  worker: Worker;
  config: IQuestionConfig;
  isAnswered: boolean;
}

function RequestQuestionTitle(props: IRequestQuestionTitleProps) {
  const { question, worker, config, isAnswered } = props;

  const { answerMode, useSummaryQuestionText } = config;

  const { text, summary } = question;

  const valueNotKnownQuestion = question as unknown as ValueNotKnownQuestion;
  const conceptReviewQuestion = question as unknown as ReviewConceptQuestion;

  const lexicalPath = (valueNotKnownQuestion || conceptReviewQuestion)
    .lexicalPath;

  const processed = FormattingUtil.processQuestionV1(
    text || '',
    lexicalPath || null
  );

  if (answerMode !== QuestionAnswerMode.SINGLE) {
    return null;
  }

  let title = null;

  if (useSummaryQuestionText && summary) {
    title = <Layout.Header>{summary}</Layout.Header>;
  }

  title = (
    <Layout.Header>
      <InlineFacts text={processed?.text!} knowledgeId={worker!.knowledgeId!} />
    </Layout.Header>
  );

  return (
    <Alert
      message={
        <div className={styles.questionTitleWrapper} data-cy="question-title">
          {title}
        </div>
      }
      icon={
        isAnswered ? <IssuesCloseOutlined /> : <ExclamationCircleOutlined />
      }
      showIcon
      type={isAnswered ? 'success' : 'warning'}
    />
  );
}

export default RequestQuestionTitle;
